import { Bell } from 'lucide-react';
import { Button } from '@marine/ui/button';
import { Badge } from '@marine/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@marine/ui/tabs';
import {
  Sheet, SheetBody, SheetContent, SheetFooter, SheetHeader, SheetTitle, SheetTrigger,
} from '@marine/ui/sheet';
import { NotificationRow } from './NotificationRow';
import { useNotifications, useUnreadCount, useMarkAllRead, useMarkRead } from './useNotifications';
import { useNotificationChannel } from './useNotificationChannel';
import type { AppNotification } from './types';

function List({ filter }: { filter?: 'unread' }) {
  const { data } = useNotifications(filter ? { filter } : {});
  const markRead = useMarkRead();
  // getPaginated returns PaginatedResponse { items, meta } — read `.items`, not `.data`
  // (matches EnquiriesInboxPage.tsx: `data?.items`).
  const rows = (data?.items ?? []) as AppNotification[];
  if (rows.length === 0) {
    return <p className="px-4 py-8 text-center text-sm text-muted-foreground">You're all caught up.</p>;
  }
  return (
    <div className="flex flex-col divide-y divide-border">
      {rows.map((n) => (
        <NotificationRow
          key={n.id}
          notification={n}
          // #742 — opening a notification is what marks it read. The mutation
          // lives here rather than in the row so the row stays presentational,
          // which is the same split NotificationActions already follows.
          onNavigate={n.is_read ? undefined : () => markRead.mutate(n.id)}
        />
      ))}
    </div>
  );
}

export function NotificationsSheet() {
  useNotificationChannel();
  const { data: unread = 0 } = useUnreadCount();
  const markAll = useMarkAllRead();

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="ghost" mode="icon" size="sm" shape="circle" className="relative size-9" aria-label="Notifications">
          <Bell className="size-4.5" />
          {unread > 0 && (
            <Badge className="absolute -end-0.5 -top-0.5 min-w-4 px-1 py-0 text-[10px]" variant="destructive">
              {unread > 99 ? '99+' : unread}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent variant="panel" className="w-full sm:w-[440px]">
        <SheetHeader className="mb-0">
          <SheetTitle className="p-4">Notifications</SheetTitle>
        </SheetHeader>
        <SheetBody className="grow p-0">
          <Tabs defaultValue="all" className="w-full">
            <TabsList variant="line" className="w-full px-4">
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="unread">Unread</TabsTrigger>
            </TabsList>
            <TabsContent value="all" className="mt-0"><List /></TabsContent>
            <TabsContent value="unread" className="mt-0"><List filter="unread" /></TabsContent>
          </Tabs>
        </SheetBody>
        <SheetFooter className="border-t border-border p-4">
          <Button variant="outline" className="w-full" onClick={() => markAll.mutate()} disabled={unread === 0}>
            Mark all as read
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
